import type { ParamSchema, ParamValue, ParamValues } from './types';
import { clamp } from './math/scalar';

/**
 * Parameter plumbing.
 *
 * Values arriving from the UI, a preset file or a generated design are all
 * untrusted, so everything funnels through `resolveParams` before it reaches a
 * pattern. A pattern therefore never has to defend itself against a missing key
 * or an out-of-range slider.
 */

export function defaultParamValues(schemas: readonly ParamSchema[]): ParamValues {
  const values: ParamValues = {};
  for (const schema of schemas) {
    values[schema.key] = schema.default;
  }
  return values;
}

/** Coerces and range-checks a single value against its schema. */
export function coerceParamValue(schema: ParamSchema, raw: unknown): ParamValue {
  switch (schema.kind) {
    case 'number':
    case 'span': {
      const value = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(value)) return schema.default;
      return clamp(value, schema.min, schema.max);
    }
    case 'int': {
      const value = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(value)) return schema.default;
      return Math.round(clamp(value, schema.min, schema.max));
    }
    case 'angle': {
      const value = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(value)) return schema.default;
      // Angles wrap instead of clamping so a dial can be spun freely.
      return ((value % 360) + 360) % 360;
    }
    case 'boolean':
      return typeof raw === 'boolean' ? raw : raw === 'true' || raw === 1;
    case 'select': {
      const value = String(raw);
      return schema.options.some((option) => option.value === value) ? value : schema.default;
    }
    case 'asset':
      // Asset ids are validated when the field is resolved against the design's
      // asset table, not here: a design may legitimately be loaded before its
      // bitmaps have been decoded.
      return typeof raw === 'string' ? raw : schema.default;
  }
}

/**
 * Produces a complete, valid value set: every declared key is present, unknown
 * keys are dropped and every value sits inside its declared range.
 */
export function resolveParams(
  schemas: readonly ParamSchema[],
  values: Readonly<ParamValues> | undefined,
): ParamValues {
  const resolved: ParamValues = {};
  for (const schema of schemas) {
    const raw = values?.[schema.key];
    resolved[schema.key] = raw === undefined ? schema.default : coerceParamValue(schema, raw);
  }
  return resolved;
}

/**
 * Converts a `span` parameter into millimetres.
 *
 * This is the hinge that keeps multi-panel motifs aligned: a span of `2` always
 * means "two panel pitches", so the motif still meets the panel joints after the
 * panel size is changed.
 */
export function spanToMillimetres(span: number, pitch: number): number {
  return Math.max(span * pitch, 1e-3);
}
